import { ButtonProps } from "@repo/core/components/core/Button/types";
import { IActionsheetRefProps } from "@repo/core/components/layout/Actionsheet/types";
import { RequiredDocumentTypeEnum } from "@repo/core/enums/proposals";
import { useFileSelector } from "@repo/core/hooks/useFileSelector";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { usePreProposal } from "../../hooks/usePreProposal";
import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import {
  getManualFlowDocuments,
  getPendingManualDocuments
} from "@repo/core/screens/PreProposal/screens/BiometryValidation/utils";
import { useUploadProposalDocument } from "@repo/core/hooks/useUploadProposalDocument";
import { useDocumentCaptureStore } from "@repo/core/stores/documentCapture";
import useToast from "@repo/core/components/core/Toast";
import { useCreditProposal } from "@repo/core/screens/ProposalEffectiveFlows/Credit/hooks/useCreditProposal";
import { navigateToReanalysisCreditFlowIfApplicable } from "../BiometryValidation/services/navigation.service";
import { getApiErrorMessage } from "../../services/getApiErrorMessage.service";
import { resolveManualCaptureNavigation } from "./manualCaptureNavigation";

const isManualBiometryDocument = (
  documentType?: RequiredDocumentTypeEnum | null
) =>
  documentType === RequiredDocumentTypeEnum.BIOMETRY ||
  documentType === RequiredDocumentTypeEnum.BIOMETRY_FACE;

export const SelectDocumentsController = () => {
  const { push, back, params, replace, resetTo } = useNavigation();
  const { clearCreditProposal } = useCreditProposal();
  const toast = useToast();
  const {
    requiredDocuments,
    proposalData,
    setProposalData,
    hasBiometryDocuments,
    clearPreProposal
  } = usePreProposal();
  const { selectFile, selectedFile, isLoading } = useFileSelector();
  const { uploadDocumentFromBase64 } = useUploadProposalDocument();
  const documentCaptureStore = useDocumentCaptureStore();
  const actionSheetRef = useRef<IActionsheetRefProps>(null);

  const isRestartRequired = useMemo(() => {
    return (
      params?.restartRequired === true ||
      params?.restartRequired === "true" ||
      proposalData?.restartRequired === true
    );
  }, [params, proposalData?.restartRequired]);

  const forceManualCapture = useMemo(() => {
    return (
      params?.forceManualCapture === true ||
      params?.forceManualCapture === "true" ||
      params?.biometryFailed === true ||
      params?.biometryFailed === "true" ||
      proposalData?.shouldForceManualCapture === true ||
      proposalData?.unicoStatus === "unavailable"
    );
  }, [
    params,
    proposalData?.shouldForceManualCapture,
    proposalData?.unicoStatus
  ]);

  const isUnicoUnavailable = useMemo(() => {
    return (
      params?.unicoUnavailable === true ||
      params?.unicoUnavailable === "true" ||
      proposalData?.unicoStatus === "unavailable"
    );
  }, [params, proposalData?.unicoStatus]);

  const pendingManualDocuments = useMemo(() => {
    if (!requiredDocuments) return [];
    return getPendingManualDocuments(requiredDocuments, {
      includePendingUnicoDocuments: forceManualCapture,
      unicoUploadedIdentityDocumentTypes:
        proposalData?.unicoUploadedIdentityDocumentTypes,
      excludeDocumentTypesFromManual:
        proposalData?.manualCaptureExcludedDocumentTypes
    });
  }, [
    forceManualCapture,
    proposalData?.unicoUploadedIdentityDocumentTypes,
    proposalData?.manualCaptureExcludedDocumentTypes,
    requiredDocuments
  ]);

  const getManualFlowDocumentsOptions = useMemo(
    () => ({
      includePendingUnicoDocuments: forceManualCapture,
      unicoUploadedIdentityDocumentTypes:
        proposalData?.unicoUploadedIdentityDocumentTypes,
      excludeDocumentTypesFromManual:
        proposalData?.manualCaptureExcludedDocumentTypes
    }),
    [
      forceManualCapture,
      proposalData?.unicoUploadedIdentityDocumentTypes,
      proposalData?.manualCaptureExcludedDocumentTypes
    ]
  );

  const manualFlowDocuments = useMemo(() => {
    if (!requiredDocuments) return [];
    return getManualFlowDocuments(
      requiredDocuments,
      getManualFlowDocumentsOptions
    );
  }, [requiredDocuments, getManualFlowDocumentsOptions]);

  // Track current document being captured
  const [currentDocumentCapture, setCurrentDocumentCaptureState] =
    useState<RequiredDocumentTypeEnum | null>(null);
  const [capturedFiles, setCapturedFiles] = useState<
    Record<string, string | null>
  >({});
  // Track upload loading state per document type
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({});

  // Check if all pending documents have been uploaded
  // Only consider documents that are actually pending (not uploaded)
  const allPendingDocumentsUploaded = useMemo(() => {
    if (isRestartRequired) {
      return true;
    }

    const pendingDocs = pendingManualDocuments.filter(
      (doc) => doc.isUploaded !== true
    );
    return (
      pendingDocs.length === 0 ||
      pendingDocs.every(
        (doc) => doc.isUploaded === true || capturedFiles[doc.strTipoDocumento]
      )
    );
  }, [capturedFiles, isRestartRequired, pendingManualDocuments]);

  // Handle captured image from CaptureDocument screen when returning
  // This effect is now simplified since upload happens directly in CaptureDocument
  useEffect(() => {
    if (!currentDocumentCapture) return;

    // Check if there's a captured document in the store
    const capturedDoc = documentCaptureStore.requiredDocuments.find(
      (doc) =>
        doc.strTipoDocumento === currentDocumentCapture && doc.selectedFile
    );

    if (capturedDoc?.selectedFile) {
      // Clear the captured document from store first to prevent re-processing
      documentCaptureStore.resetDocumentCapture();
      setCurrentDocumentCaptureState(null);
      // Upload should already be done, but keep this as fallback
    }
  }, [
    documentCaptureStore,
    documentCaptureStore.requiredDocuments.length,
    currentDocumentCapture
  ]);

  const handleUploadDocument = useCallback(
    async (docType: RequiredDocumentTypeEnum, file: string) => {
      // Set loading state
      setIsUploading((prev) => ({ ...prev, [docType]: true }));

      // Store the file temporarily
      setCapturedFiles((prev) => ({
        ...prev,
        [docType]: file
      }));

      // Upload the document
      try {
        if (!proposalData?.lngProposta) {
          toast.error("Proposta não encontrada. Tente novamente.");
          setIsUploading((prev) => ({ ...prev, [docType]: false }));
          return;
        }

        await uploadDocumentFromBase64(proposalData.lngProposta, file, {
          strTipoDocumento: docType,
          strObservacoes: `Documento capturado manualmente - ${docType}`,
          strCliente: proposalData.strCliente || "",
          strCpfPortador: proposalData.strCliente || "",
          strAutorizacao: "S",
          datDocumentoExterno: new Date().toISOString(),
          strVisualizarWEB: "S"
        });

        toast.success("Documento enviado com sucesso!");
        setCurrentDocumentCaptureState(null);
      } catch (error) {
        console.error("Error uploading document:", error);
        toast.error(
          getApiErrorMessage(error) ||
            "Erro ao enviar documento. Tente novamente."
        );
        // Remove from captured files on error
        setCapturedFiles((prev) => ({
          ...prev,
          [docType]: null
        }));
      } finally {
        setIsUploading((prev) => ({ ...prev, [docType]: false }));
      }
    },
    [
      proposalData?.lngProposta,
      proposalData?.strCliente,
      uploadDocumentFromBase64,
      toast
    ]
  );

  const handleCaptureDocument = async (
    docTypeParam?: RequiredDocumentTypeEnum
  ) => {
    const captureNavigation = resolveManualCaptureNavigation(
      docTypeParam,
      currentDocumentCapture
    );
    if (!captureNavigation || isRestartRequired) return;

    const { documentType, cameraFacing, shouldDismissActionSheet } =
      captureNavigation;

    // Keep the local selection in sync even when biometry opens the camera
    // directly. This allows the return effect to clear the completed capture.
    setCurrentDocumentCaptureState(documentType);

    // Resolve from full list so uploaded items can be replaced
    const documentToCapture = requiredDocuments?.find(
      (doc) => doc.strTipoDocumento === documentType
    );

    if (documentToCapture) {
      // Add document to capture store temporarily so CaptureDocument can save it
      documentCaptureStore.setRequiredDocuments([
        {
          ...documentToCapture,
          selectedFile: null
        }
      ]);
      documentCaptureStore.setCurrentDocumentCapture(documentType);
    }

    push(ROUTES.preProposalsCaptureDocument, {
      cameraFacing
    });
    if (shouldDismissActionSheet) {
      actionSheetRef.current?.close();
    }
  };

  const handleSelectDocument = (document: RequiredDocumentTypeEnum) => {
    if (isRestartRequired) {
      return;
    }

    // If it's selfie/biometry, open capture directly without bottom sheet
    if (isManualBiometryDocument(document)) {
      handleCaptureDocument(document);
      return;
    }

    // For other documents, set state and open bottom sheet
    setCurrentDocumentCaptureState(document);
    actionSheetRef.current?.open();
  };

  const onSelectFile = async (file: string | null) => {
    const docType = currentDocumentCapture;
    if (!docType || !file) {
      actionSheetRef.current?.close();
      return;
    }

    // Close bottom sheet immediately
    actionSheetRef.current?.close();

    // Upload in background
    handleUploadDocument(docType, file);
  };

  const handleContinue = async () => {
    if (isRestartRequired) {
      clearPreProposal();
      replace(ROUTES.home);
      return;
    }

    // Use pendingManualDocuments (the documents shown to user) for validation,
    // not all requiredDocuments - when biometry fails, some docs (e.g. doc back)
    // may be excluded from manual flow but still in requiredDocuments
    const pendingFromManual = pendingManualDocuments.filter(
      (doc) => doc.isUploaded !== true
    );
    if (pendingFromManual.length > 0) {
      toast.error(
        `Ainda existem ${pendingFromManual.length} documento(s) pendente(s). Por favor, envie todos os documentos antes de continuar.`
      );
      return;
    }

    // Manual document upload does not change proposal fields — only update local flow flags.
    setProposalData(
      {
        shouldForceManualCapture: false,
        restartRequired: false,
        unicoStatus: undefined
      },
      { skipAlterarSync: true }
    );

    if (
      !navigateToReanalysisCreditFlowIfApplicable(
        proposalData,
        resetTo,
        clearCreditProposal
      )
    ) {
      //TODO REMOVE WHEN preProposalsSelectFinancialService THE FLOW RETURNS
      // push(ROUTES.preProposalsSelectFinancialService);
      replace(ROUTES.preProposalsVerifyInformations);
    }
  };

  const handleGoBack = () => {
    if (isRestartRequired) {
      clearPreProposal();
      replace(ROUTES.home);
      return;
    }

    if (hasBiometryDocuments()) {
      toast.success(
        "Biometria validada!",
        "Você já realizou o processo de biometria."
      );
      return;
    }
    back();
  };
  const onClickCancel = () => {
    clearPreProposal();
    replace(ROUTES.home);
  };

  const handleOnPressOption = (action: string) => {
    switch (action) {
      case "$captureImage":
        handleCaptureDocument();
        break;
      case "$uploadFile":
        selectFile(onSelectFile);
        break;
      default:
        console.log("action not found");
        break;
    }
  };

  const footerOptions: ButtonProps[] = [
    {
      text: "Continuar",
      onPress: handleContinue,
      action: "secondary",
      isDisabled: !allPendingDocumentsUploaded || isLoading
    }
  ];

  return {
    selectFile,
    selectedFile,
    isLoading,
    handleCaptureDocument,
    handleContinue,
    handleGoBack,
    requiredDocuments: manualFlowDocuments,
    actionSheetRef,
    handleOnPressOption,
    handleSelectDocument,
    allPendingDocumentsUploaded,
    footerOptions,
    isUploading,
    handleUploadDocument,
    onClickCancel,
    hasBiometryDocuments,
    isRestartRequired,
    isUnicoUnavailable,
    forceManualCapture
  };
};
